import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PubQuiz as PubQuizType, QuizRound, QuizQuestion, QuizTeam, QuizQuestionType, RebusType } from '../types';
import { useSpotify } from '../context/SpotifyContext';
import './PubQuiz.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Spotify track interface for search results
interface SpotifyTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  year: number | null;
  duration: number;
  thumbnail: string | null;
  previewUrl: string | null;
  spotifyUrl: string;
}

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

const PubQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { play, pause, isAuthenticated, isPremium, isReady } = useSpotify();
  
  // Quiz state
  const [quizzes, setQuizzes] = useState<PubQuizType[]>([]);
  const [selectedQuiz, setSelectedQuiz] = useState<PubQuizType | null>(null);
  const [selectedRound, setSelectedRound] = useState<QuizRound | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<QuizQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Editing state
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<Partial<PubQuizType>>({});
  const [editingRound, setEditingRound] = useState<Partial<QuizRound>>({});
  const [editingQuestion, setEditingQuestion] = useState<Partial<QuizQuestion>>({});
  const [editingTeam, setEditingTeam] = useState<Partial<QuizTeam>>({});
  
  // Play state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [timer, setTimer] = useState<number | null>(null);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Spotify search state
  const [showSpotifySearch, setShowSpotifySearch] = useState(false);
  const [spotifyQuery, setSpotifyQuery] = useState('');
  const [spotifyResults, setSpotifyResults] = useState<SpotifyTrack[]>([]);
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  
  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Audio ref
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Load quizzes
  const loadQuizzes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/pubquiz`);
      if (response.ok) {
        const data = await response.json();
        setQuizzes(data);
      }
    } catch (err) {
      console.error('Failed to load quizzes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuizzes();
  }, [loadQuizzes]);

  // Spotify search function
  const searchSpotify = async () => {
    if (!spotifyQuery.trim()) return;
    
    setSpotifyLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/spotify/search?query=${encodeURIComponent(spotifyQuery)}&limit=10`);
      const data = await response.json();
      if (data.results) {
        setSpotifyResults(data.results);
      }
    } catch (err) {
      console.error('Spotify search failed:', err);
    } finally {
      setSpotifyLoading(false);
    }
  };

  // Select Spotify track for question
  const selectSpotifyTrack = (track: SpotifyTrack) => {
    setEditingQuestion({
      ...editingQuestion,
      spotifyUri: `spotify:track:${track.id}`,
      audioUrl: track.previewUrl || undefined,
      imageUrl: editingQuestion.imageUrl || track.thumbnail || undefined
    });
    setShowSpotifySearch(false);
    setSpotifyResults([]);
    setSpotifyQuery('');
  };

  // Image upload function
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setEditingQuestion({
          ...editingQuestion,
          imageUrl: `${API_BASE}${data.imageUrl}`
        });
      } else {
        console.error('Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setImageUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Save quiz
  const saveQuiz = async (quiz: PubQuizType) => {
    try {
      const response = await fetch(`${API_BASE}/api/pubquiz/${quiz.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quiz)
      });
      if (response.ok) {
        await loadQuizzes();
        // Update selected quiz
        const updated = await response.json();
        setSelectedQuiz(updated);
        if (selectedRound) {
          const newRound = updated.rounds.find((r: QuizRound) => r.id === selectedRound.id);
          setSelectedRound(newRound || null);
        }
      }
    } catch (err) {
      console.error('Failed to save quiz:', err);
    }
  };

  // Create quiz
  const createQuiz = async () => {
    const newQuiz: PubQuizType = {
      id: generateId(),
      name: editingQuiz.name || 'Nieuwe Quiz',
      date: editingQuiz.date || new Date().toISOString().split('T')[0],
      description: editingQuiz.description,
      rounds: [],
      teams: [],
      currentRoundIndex: 0,
      currentQuestionIndex: 0,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      const response = await fetch(`${API_BASE}/api/pubquiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newQuiz)
      });
      if (response.ok) {
        await loadQuizzes();
        setShowQuizModal(false);
        setEditingQuiz({});
      }
    } catch (err) {
      console.error('Failed to create quiz:', err);
    }
  };

  // Delete quiz
  const deleteQuiz = async (quizId: string) => {
    if (!window.confirm('Weet je zeker dat je deze quiz wilt verwijderen?')) return;
    
    try {
      await fetch(`${API_BASE}/api/pubquiz/${quizId}`, { method: 'DELETE' });
      await loadQuizzes();
      if (selectedQuiz?.id === quizId) {
        setSelectedQuiz(null);
        setSelectedRound(null);
      }
    } catch (err) {
      console.error('Failed to delete quiz:', err);
    }
  };

  // Add round
  const addRound = () => {
    if (!selectedQuiz) return;
    
    const newRound: QuizRound = {
      id: generateId(),
      name: editingRound.name || `Ronde ${selectedQuiz.rounds.length + 1}`,
      description: editingRound.description,
      order: selectedQuiz.rounds.length + 1,
      questions: [],
      type: editingRound.type || 'standard'
    };

    const updatedQuiz = {
      ...selectedQuiz,
      rounds: [...selectedQuiz.rounds, newRound],
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
    setShowRoundModal(false);
    setEditingRound({});
  };

  // Delete round
  const deleteRound = (roundId: string) => {
    if (!selectedQuiz) return;
    if (!window.confirm('Weet je zeker dat je deze ronde wilt verwijderen?')) return;

    const updatedQuiz = {
      ...selectedQuiz,
      rounds: selectedQuiz.rounds.filter(r => r.id !== roundId),
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
    if (selectedRound?.id === roundId) {
      setSelectedRound(null);
    }
  };

  // Add question
  const addQuestion = () => {
    if (!selectedQuiz || !selectedRound) return;

    const newQuestion: QuizQuestion = {
      id: generateId(),
      type: editingQuestion.type || 'multiple_choice',
      question: editingQuestion.question || '',
      options: editingQuestion.type === 'multiple_choice' ? (editingQuestion.options || ['', '', '', '']) : undefined,
      correctAnswer: editingQuestion.correctAnswer || '',
      points: editingQuestion.points || 1,
      timeLimit: editingQuestion.timeLimit,
      imageUrl: editingQuestion.imageUrl,
      audioUrl: editingQuestion.audioUrl,
      spotifyUri: editingQuestion.spotifyUri,
      youtubeVideoId: editingQuestion.youtubeVideoId,
      youtubeStartTime: editingQuestion.youtubeStartTime,
      youtubeEndTime: editingQuestion.youtubeEndTime,
      hint: editingQuestion.hint,
      category: editingQuestion.category,
      order: selectedRound.questions.length + 1,
      // Rebus fields
      rebusType: editingQuestion.rebusType,
      rebusContent: editingQuestion.rebusContent,
      rebusHints: editingQuestion.rebusHints,
      // Spotlight fields
      spotlightEnabled: editingQuestion.spotlightEnabled,
      spotlightSpeed: editingQuestion.spotlightSpeed,
      spotlightSize: editingQuestion.spotlightSize,
      // Puzzle fields
      puzzleEnabled: editingQuestion.puzzleEnabled,
      puzzleGridSize: editingQuestion.puzzleGridSize,
      puzzleSpeed: editingQuestion.puzzleSpeed,
    };

    const updatedRounds = selectedQuiz.rounds.map(r => 
      r.id === selectedRound.id 
        ? { ...r, questions: [...r.questions, newQuestion] }
        : r
    );

    const updatedQuiz = {
      ...selectedQuiz,
      rounds: updatedRounds,
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
    setShowQuestionModal(false);
    setEditingQuestion({});
  };

  // Update question
  const updateQuestion = () => {
    if (!selectedQuiz || !selectedRound || !editingQuestion.id) return;

    const updatedRounds = selectedQuiz.rounds.map(r => 
      r.id === selectedRound.id 
        ? { 
            ...r, 
            questions: r.questions.map(q => 
              q.id === editingQuestion.id ? { ...q, ...editingQuestion } as QuizQuestion : q
            )
          }
        : r
    );

    const updatedQuiz = {
      ...selectedQuiz,
      rounds: updatedRounds,
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
    setShowQuestionModal(false);
    setEditingQuestion({});
  };

  // Delete question
  const deleteQuestion = (questionId: string) => {
    if (!selectedQuiz || !selectedRound) return;
    if (!window.confirm('Weet je zeker dat je deze vraag wilt verwijderen?')) return;

    const updatedRounds = selectedQuiz.rounds.map(r => 
      r.id === selectedRound.id 
        ? { ...r, questions: r.questions.filter(q => q.id !== questionId) }
        : r
    );

    const updatedQuiz = {
      ...selectedQuiz,
      rounds: updatedRounds,
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
  };

  // Add team
  const addTeam = () => {
    if (!selectedQuiz) return;

    const newTeam: QuizTeam = {
      id: generateId(),
      name: editingTeam.name || `Team ${(selectedQuiz.teams?.length || 0) + 1}`,
      score: 0,
      roundScores: {}
    };

    const updatedQuiz = {
      ...selectedQuiz,
      teams: [...(selectedQuiz.teams || []), newTeam],
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
    setShowTeamModal(false);
    setEditingTeam({});
  };

  // Delete team
  const deleteTeam = (teamId: string) => {
    if (!selectedQuiz) return;
    if (!window.confirm('Weet je zeker dat je dit team wilt verwijderen?')) return;

    const updatedQuiz = {
      ...selectedQuiz,
      teams: selectedQuiz.teams?.filter(t => t.id !== teamId) || [],
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
  };

  // Update team score
  const updateTeamScore = (teamId: string, points: number) => {
    if (!selectedQuiz) return;

    const updatedQuiz = {
      ...selectedQuiz,
      teams: selectedQuiz.teams?.map(t => 
        t.id === teamId 
          ? { ...t, score: Math.max(0, t.score + points) }
          : t
      ) || [],
      updatedAt: new Date().toISOString()
    };

    saveQuiz(updatedQuiz);
  };

  // Show question on display
  const showQuestionOnDisplay = async (question: QuizQuestion, showingAnswer: boolean = false) => {
    try {
      await fetch(`${API_BASE}/api/display-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'pubquiz',
          quizQuestion: {
            ...question,
            showAnswer: showingAnswer
          },
          quizName: selectedQuiz?.name,
          roundName: selectedRound?.name
        })
      });
    } catch (err) {
      console.error('Failed to update display:', err);
    }
  };

  // Play question (show on display)
  const playQuestion = async (question: QuizQuestion) => {
    setSelectedQuestion(question);
    setShowAnswer(false);
    setIsPlaying(true);
    
    // Show on display
    await showQuestionOnDisplay(question, false);
    
    // Start timer if configured
    if (question.timeLimit) {
      setTimer(question.timeLimit);
      const interval = setInterval(() => {
        setTimer(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setTimerInterval(interval);
    }
    
    // Play audio if available
    if (question.spotifyUri && isAuthenticated && isPremium && isReady) {
      await play(question.spotifyUri);
    } else if (question.audioUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(question.audioUrl);
      audioRef.current = audio;
      audio.play();
    }
  };

  // Reveal answer
  const revealAnswer = async () => {
    if (!selectedQuestion) return;
    
    setShowAnswer(true);
    
    // Stop timer
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    
    // Stop audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    await pause();
    
    // Update display
    await showQuestionOnDisplay(selectedQuestion, true);
  };

  // Stop playing
  const stopPlaying = async () => {
    setIsPlaying(false);
    setSelectedQuestion(null);
    setShowAnswer(false);
    setTimer(null);
    
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    await pause();
    
    // Clear display
    try {
      await fetch(`${API_BASE}/api/display-state/stop`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to stop display:', err);
    }
  };

  // Question type labels
  const questionTypeLabels: Record<QuizQuestionType, string> = {
    'multiple_choice': 'Meerkeuze',
    'open': 'Open vraag',
    'true_false': 'Waar/Niet waar',
    'number': 'Getal',
    'music': 'Muziek',
    'rebus': '🧩 Rebus',
    'video': '🎬 Video'
  };

  // Rebus type labels
  const rebusTypeLabels: Record<RebusType, string> = {
    'emoji': '🎭 Emoji raadsel',
    'missing_words': '📝 Missende woorden',
    'scrambled': '🔀 Door elkaar',
    'fragments': '🎵 Fragmenten'
  };

  if (loading) {
    return <div className="pubquiz-page loading">Laden...</div>;
  }

  return (
    <div className="pubquiz-page">
      {/* Header */}
      <div className="quiz-header">
        <div className="header-title-group">
          <button className="back-btn" onClick={() => navigate('/spotify-dashboard')}>
            ← Terug
          </button>
          <h1>🎯 Pub Quiz</h1>
        </div>
        <button 
          className="display-btn"
          onClick={() => window.open('/display', '_blank')}
        >
          📺 Display
        </button>
      </div>

      <div className="quiz-layout">
        {/* Left Panel: Quiz List */}
        <div className="panel quiz-list-panel">
          <div className="panel-header">
            <h2>📋 Quizzen</h2>
            <button 
              className="add-btn"
              onClick={() => {
                setEditingQuiz({});
                setShowQuizModal(true);
              }}
            >
              + Nieuw
            </button>
          </div>
          
          <div className="quiz-list">
            {quizzes.map(quiz => (
              <div 
                key={quiz.id}
                className={`quiz-card ${selectedQuiz?.id === quiz.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedQuiz(quiz);
                  setSelectedRound(quiz.rounds[0] || null);
                }}
              >
                <div className="quiz-name">{quiz.name}</div>
                <div className="quiz-date">{new Date(quiz.date).toLocaleDateString('nl-NL')}</div>
                <div className="quiz-stats">
                  {quiz.rounds.length} rondes • {quiz.teams?.length || 0} teams
                </div>
                <button 
                  className="delete-btn"
                  onClick={(e) => { e.stopPropagation(); deleteQuiz(quiz.id); }}
                >
                  🗑️
                </button>
              </div>
            ))}
            {quizzes.length === 0 && (
              <div className="empty-state">
                Geen quizzen. Maak je eerste quiz!
              </div>
            )}
          </div>
        </div>

        {/* Middle Panel: Rounds & Questions */}
        {selectedQuiz && (
          <div className="panel rounds-panel">
            <div className="panel-header">
              <h2>📝 {selectedQuiz.name}</h2>
              <button 
                className="add-btn"
                onClick={() => {
                  setEditingRound({});
                  setShowRoundModal(true);
                }}
              >
                + Ronde
              </button>
            </div>

            {/* Round tabs */}
            <div className="round-tabs">
              {selectedQuiz.rounds.map(round => (
                <button
                  key={round.id}
                  className={`round-tab ${selectedRound?.id === round.id ? 'active' : ''}`}
                  onClick={() => setSelectedRound(round)}
                >
                  {round.name}
                  <span className="question-count">({round.questions.length})</span>
                </button>
              ))}
            </div>

            {/* Questions list */}
            {selectedRound && (
              <div className="questions-section">
                <div className="section-header">
                  <h3>{selectedRound.name}</h3>
                  <div className="section-actions">
                    <button 
                      className="add-btn small"
                      onClick={() => {
                        setEditingQuestion({ type: 'multiple_choice', points: 1 });
                        setShowQuestionModal(true);
                      }}
                    >
                      + Vraag
                    </button>
                    <button 
                      className="delete-btn small"
                      onClick={() => deleteRound(selectedRound.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="questions-list">
                  {selectedRound.questions.map((question, index) => (
                    <div 
                      key={question.id}
                      className={`question-card ${selectedQuestion?.id === question.id ? 'active' : ''}`}
                    >
                      <div className="question-number">{index + 1}</div>
                      <div className="question-content">
                        <div className="question-text">{question.question}</div>
                        <div className="question-meta">
                          <span className="question-type">{questionTypeLabels[question.type]}</span>
                          <span className="question-points">{question.points} pt</span>
                          {question.timeLimit && <span className="question-time">⏱️ {question.timeLimit}s</span>}
                        </div>
                      </div>
                      <div className="question-actions">
                        <button 
                          className="play-btn"
                          onClick={() => playQuestion(question)}
                        >
                          ▶️
                        </button>
                        <button 
                          className="edit-btn"
                          onClick={() => {
                            setEditingQuestion(question);
                            setShowQuestionModal(true);
                          }}
                        >
                          ✏️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => deleteQuestion(question.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                  {selectedRound.questions.length === 0 && (
                    <div className="empty-state">
                      Nog geen vragen in deze ronde.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right Panel: Teams & Playing */}
        {selectedQuiz && (
          <div className="panel teams-panel">
            {/* Playing section */}
            {isPlaying && selectedQuestion && (
              <div className="playing-section">
                <div className="playing-header">
                  <h3>🎯 Nu speelt</h3>
                  <button className="stop-btn" onClick={stopPlaying}>⏹️ Stop</button>
                </div>
                
                <div className="playing-question">
                  <div className="question-display">
                    {selectedQuestion.question}
                  </div>
                  
                  {selectedQuestion.type === 'multiple_choice' && selectedQuestion.options && (
                    <div className="options-display">
                      {selectedQuestion.options.map((opt, i) => (
                        <div 
                          key={i} 
                          className={`option ${showAnswer && opt === selectedQuestion.correctAnswer ? 'correct' : ''}`}
                        >
                          {String.fromCharCode(65 + i)}. {opt}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {timer !== null && (
                    <div className={`timer ${timer <= 5 ? 'warning' : ''}`}>
                      ⏱️ {timer}s
                    </div>
                  )}
                  
                  {showAnswer && (
                    <div className="answer-display">
                      ✅ {selectedQuestion.correctAnswer}
                    </div>
                  )}
                </div>
                
                {!showAnswer && (
                  <button className="reveal-btn" onClick={revealAnswer}>
                    👁️ Toon antwoord
                  </button>
                )}
              </div>
            )}

            {/* Teams section */}
            <div className="teams-section">
              <div className="section-header">
                <h3>👥 Teams</h3>
                <button 
                  className="add-btn small"
                  onClick={() => {
                    setEditingTeam({});
                    setShowTeamModal(true);
                  }}
                >
                  + Team
                </button>
              </div>
              
              <div className="teams-list">
                {selectedQuiz.teams?.sort((a, b) => b.score - a.score).map((team, index) => (
                  <div key={team.id} className="team-card">
                    <div className="team-rank">#{index + 1}</div>
                    <div className="team-info">
                      <div className="team-name">{team.name}</div>
                      <div className="team-score">{team.score} punten</div>
                    </div>
                    <div className="team-actions">
                      <button onClick={() => updateTeamScore(team.id, -1)}>−</button>
                      <button onClick={() => updateTeamScore(team.id, 1)}>+</button>
                      <button 
                        className="delete-btn"
                        onClick={() => deleteTeam(team.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                {(!selectedQuiz.teams || selectedQuiz.teams.length === 0) && (
                  <div className="empty-state">
                    Nog geen teams toegevoegd.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quiz Modal */}
      {showQuizModal && (
        <div className="modal-overlay" onClick={() => setShowQuizModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nieuwe Quiz</h2>
            <div className="form-group">
              <label>Naam</label>
              <input
                type="text"
                value={editingQuiz.name || ''}
                onChange={e => setEditingQuiz({ ...editingQuiz, name: e.target.value })}
                placeholder="Quiz naam"
              />
            </div>
            <div className="form-group">
              <label>Datum</label>
              <input
                type="date"
                value={editingQuiz.date || new Date().toISOString().split('T')[0]}
                onChange={e => setEditingQuiz({ ...editingQuiz, date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Beschrijving</label>
              <textarea
                value={editingQuiz.description || ''}
                onChange={e => setEditingQuiz({ ...editingQuiz, description: e.target.value })}
                placeholder="Optionele beschrijving"
              />
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowQuizModal(false)}>Annuleren</button>
              <button className="save-btn" onClick={createQuiz}>Opslaan</button>
            </div>
          </div>
        </div>
      )}

      {/* Round Modal */}
      {showRoundModal && (
        <div className="modal-overlay" onClick={() => setShowRoundModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nieuwe Ronde</h2>
            <div className="form-group">
              <label>Naam</label>
              <input
                type="text"
                value={editingRound.name || ''}
                onChange={e => setEditingRound({ ...editingRound, name: e.target.value })}
                placeholder="Ronde naam"
              />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={editingRound.type || 'standard'}
                onChange={e => setEditingRound({ ...editingRound, type: e.target.value as QuizRound['type'] })}
              >
                <option value="standard">Standaard</option>
                <option value="music">Muziek</option>
                <option value="picture">Afbeeldingen</option>
                <option value="speed">Snelronde</option>
              </select>
            </div>
            <div className="form-group">
              <label>Beschrijving</label>
              <textarea
                value={editingRound.description || ''}
                onChange={e => setEditingRound({ ...editingRound, description: e.target.value })}
                placeholder="Optionele beschrijving"
              />
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowRoundModal(false)}>Annuleren</button>
              <button className="save-btn" onClick={addRound}>Toevoegen</button>
            </div>
          </div>
        </div>
      )}

      {/* Question Modal */}
      {showQuestionModal && (
        <div className="modal-overlay" onClick={() => setShowQuestionModal(false)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <h2>{editingQuestion.id ? 'Vraag bewerken' : 'Nieuwe Vraag'}</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select
                  value={editingQuestion.type || 'multiple_choice'}
                  onChange={e => setEditingQuestion({ 
                    ...editingQuestion, 
                    type: e.target.value as QuizQuestionType,
                    options: e.target.value === 'multiple_choice' ? ['', '', '', ''] : undefined
                  })}
                >
                  <option value="multiple_choice">Meerkeuze</option>
                  <option value="open">Open vraag</option>
                  <option value="true_false">Waar/Niet waar</option>
                  <option value="number">Getal</option>
                  <option value="music">Muziek</option>
                  <option value="rebus">🧩 Muziekrebus</option>
                  <option value="video">🎬 Video fragment</option>
                </select>
              </div>
              <div className="form-group small">
                <label>Punten</label>
                <input
                  type="number"
                  min="1"
                  value={editingQuestion.points || 1}
                  onChange={e => setEditingQuestion({ ...editingQuestion, points: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="form-group small">
                <label>Tijd (sec)</label>
                <input
                  type="number"
                  min="0"
                  value={editingQuestion.timeLimit || ''}
                  onChange={e => setEditingQuestion({ ...editingQuestion, timeLimit: parseInt(e.target.value) || undefined })}
                  placeholder="Optioneel"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Vraag</label>
              <textarea
                value={editingQuestion.question || ''}
                onChange={e => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                placeholder="Typ je vraag hier..."
                rows={3}
              />
            </div>

            {editingQuestion.type === 'multiple_choice' && (
              <div className="form-group">
                <label>Antwoordopties</label>
                {(editingQuestion.options || ['', '', '', '']).map((opt, i) => (
                  <div key={i} className="option-input">
                    <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                    <input
                      type="text"
                      value={opt}
                      onChange={e => {
                        const newOptions = [...(editingQuestion.options || ['', '', '', ''])];
                        newOptions[i] = e.target.value;
                        setEditingQuestion({ ...editingQuestion, options: newOptions });
                      }}
                      placeholder={`Optie ${String.fromCharCode(65 + i)}`}
                    />
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={editingQuestion.correctAnswer === opt && opt !== ''}
                      onChange={() => setEditingQuestion({ ...editingQuestion, correctAnswer: opt })}
                      title="Markeer als correct antwoord"
                    />
                  </div>
                ))}
              </div>
            )}

            {editingQuestion.type === 'true_false' && (
              <div className="form-group">
                <label>Correct antwoord</label>
                <div className="true-false-options">
                  <label>
                    <input
                      type="radio"
                      name="trueFalse"
                      checked={editingQuestion.correctAnswer === 'Waar'}
                      onChange={() => setEditingQuestion({ ...editingQuestion, correctAnswer: 'Waar' })}
                    />
                    Waar
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="trueFalse"
                      checked={editingQuestion.correctAnswer === 'Niet waar'}
                      onChange={() => setEditingQuestion({ ...editingQuestion, correctAnswer: 'Niet waar' })}
                    />
                    Niet waar
                  </label>
                </div>
              </div>
            )}

            {(editingQuestion.type === 'open' || editingQuestion.type === 'number' || editingQuestion.type === 'music' || editingQuestion.type === 'video') && (
              <div className="form-group">
                <label>Correct antwoord</label>
                <input
                  type={editingQuestion.type === 'number' ? 'number' : 'text'}
                  value={editingQuestion.correctAnswer || ''}
                  onChange={e => setEditingQuestion({ ...editingQuestion, correctAnswer: e.target.value })}
                  placeholder="Het correcte antwoord"
                />
              </div>
            )}

            {/* Video section */}
            {editingQuestion.type === 'video' && (
              <div className="video-section">
                <div className="form-group">
                  <label>🎬 YouTube Video</label>
                  <div className="video-input-group">
                    <input
                      type="text"
                      value={editingQuestion.youtubeVideoId || ''}
                      onChange={e => {
                        // Extract video ID from URL or use as-is
                        let videoId = e.target.value;
                        const urlMatch = videoId.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
                        if (urlMatch) videoId = urlMatch[1];
                        setEditingQuestion({ ...editingQuestion, youtubeVideoId: videoId });
                      }}
                      placeholder="YouTube URL of Video ID (bijv: dQw4w9WgXcQ)"
                    />
                  </div>
                  {editingQuestion.youtubeVideoId && (
                    <div className="video-preview">
                      <img 
                        src={`https://img.youtube.com/vi/${editingQuestion.youtubeVideoId}/mqdefault.jpg`}
                        alt="Video thumbnail"
                      />
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group small">
                    <label>⏱️ Start (sec)</label>
                    <input
                      type="number"
                      min="0"
                      value={editingQuestion.youtubeStartTime || ''}
                      onChange={e => setEditingQuestion({ ...editingQuestion, youtubeStartTime: parseInt(e.target.value) || undefined })}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-group small">
                    <label>⏹️ Eind (sec)</label>
                    <input
                      type="number"
                      min="0"
                      value={editingQuestion.youtubeEndTime || ''}
                      onChange={e => setEditingQuestion({ ...editingQuestion, youtubeEndTime: parseInt(e.target.value) || undefined })}
                      placeholder="Optioneel"
                    />
                  </div>
                </div>
                <p className="video-help">💡 Tip: Laat start/eind leeg om de hele video te tonen, of stel een fragment in.</p>
              </div>
            )}

            {/* Rebus section */}
            {editingQuestion.type === 'rebus' && (
              <div className="rebus-section">
                <div className="form-group">
                  <label>🧩 Rebus type</label>
                  <select
                    value={editingQuestion.rebusType || 'emoji'}
                    onChange={e => setEditingQuestion({ ...editingQuestion, rebusType: e.target.value as RebusType })}
                  >
                    <option value="emoji">🎭 Emoji raadsel (bijv: 👑 + 🕺 + 💡 = Dancing Queen)</option>
                    <option value="missing_words">📝 Missende woorden in songtekst</option>
                    <option value="scrambled">🔀 Letters door elkaar (anagram)</option>
                    <option value="fragments">🎵 Muziekfragmenten herkennen</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    {editingQuestion.rebusType === 'emoji' && '🎭 Emoji puzzel (gebruik emoji\'s en + tekens)'}
                    {editingQuestion.rebusType === 'missing_words' && '📝 Songtekst met ___ voor missende woorden'}
                    {editingQuestion.rebusType === 'scrambled' && '🔀 Door elkaar gehusselde letters'}
                    {editingQuestion.rebusType === 'fragments' && '🎵 Beschrijving van fragmenten'}
                    {!editingQuestion.rebusType && '🧩 Puzzel inhoud'}
                  </label>
                  <textarea
                    value={editingQuestion.rebusContent || ''}
                    onChange={e => setEditingQuestion({ ...editingQuestion, rebusContent: e.target.value })}
                    placeholder={
                      editingQuestion.rebusType === 'emoji' ? '👑 + 🕺 + 💡' :
                      editingQuestion.rebusType === 'missing_words' ? 'Is this the real ___? Is this just ___?' :
                      editingQuestion.rebusType === 'scrambled' ? 'NEEUQ NGCNIDA' :
                      'Beschrijf de fragmenten...'
                    }
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>✅ Correct antwoord (songtitel of artiest)</label>
                  <input
                    type="text"
                    value={editingQuestion.correctAnswer || ''}
                    onChange={e => setEditingQuestion({ ...editingQuestion, correctAnswer: e.target.value })}
                    placeholder="bijv: Dancing Queen - ABBA"
                  />
                </div>

                <div className="form-group">
                  <label>💡 Extra hints (optioneel, één per regel)</label>
                  <textarea
                    value={(editingQuestion.rebusHints || []).join('\n')}
                    onChange={e => setEditingQuestion({ 
                      ...editingQuestion, 
                      rebusHints: e.target.value.split('\n').filter(h => h.trim()) 
                    })}
                    placeholder="Hint 1\nHint 2\nHint 3"
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Image section */}
            <div className="form-group">
              <label>📷 Afbeelding</label>
              <div className="media-section">
                <div className="media-buttons">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button 
                    type="button"
                    className="media-btn upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageUploading}
                  >
                    {imageUploading ? '⏳ Uploaden...' : '📤 Upload afbeelding'}
                  </button>
                </div>
                <div className="media-url-input">
                  <input
                    type="text"
                    value={editingQuestion.imageUrl || ''}
                    onChange={e => setEditingQuestion({ ...editingQuestion, imageUrl: e.target.value })}
                    placeholder="Of plak een afbeelding URL"
                  />
                  {editingQuestion.imageUrl && (
                    <button 
                      type="button"
                      className="clear-btn"
                      onClick={() => setEditingQuestion({ ...editingQuestion, imageUrl: undefined })}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {editingQuestion.imageUrl && (
                  <div className="media-preview">
                    <img 
                      src={editingQuestion.imageUrl} 
                      alt="Preview" 
                      onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Spotlight effect section - shown when image or video is present */}
            {(editingQuestion.imageUrl || editingQuestion.youtubeVideoId) && (
              <div className="spotlight-section">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingQuestion.spotlightEnabled || false}
                      onChange={e => setEditingQuestion({ ...editingQuestion, spotlightEnabled: e.target.checked })}
                    />
                    <span>🔦 Spotlight effect (verbergt beeld, alleen zichtbaar waar spotlight schijnt)</span>
                  </label>
                </div>

                {editingQuestion.spotlightEnabled && (
                  <div className="spotlight-options">
                    <div className="form-row">
                      <div className="form-group">
                        <label>⚡ Snelheid</label>
                        <select
                          value={editingQuestion.spotlightSpeed || 'medium'}
                          onChange={e => setEditingQuestion({ ...editingQuestion, spotlightSpeed: e.target.value as 'slow' | 'medium' | 'fast' })}
                        >
                          <option value="slow">🐢 Langzaam (20s)</option>
                          <option value="medium">🚶 Normaal (12s)</option>
                          <option value="fast">🏃 Snel (6s)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>📐 Grootte</label>
                        <select
                          value={editingQuestion.spotlightSize || 'medium'}
                          onChange={e => setEditingQuestion({ ...editingQuestion, spotlightSize: e.target.value as 'small' | 'medium' | 'large' })}
                        >
                          <option value="small">Klein (100px)</option>
                          <option value="medium">Normaal (180px)</option>
                          <option value="large">Groot (280px)</option>
                        </select>
                      </div>
                    </div>
                    <p className="spotlight-help">💡 De spotlight beweegt kriskras over het beeld. Deelnemers moeten goed opletten!</p>
                  </div>
                )}
              </div>
            )}

            {/* Puzzle effect section - shown when image is present */}
            {editingQuestion.imageUrl && (
              <div className="puzzle-section">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingQuestion.puzzleEnabled || false}
                      onChange={e => setEditingQuestion({ ...editingQuestion, puzzleEnabled: e.target.checked })}
                    />
                    <span>🧩 Puzzel effect (afbeelding in stukjes die verschuiven)</span>
                  </label>
                </div>

                {editingQuestion.puzzleEnabled && (
                  <div className="puzzle-options">
                    <div className="form-row">
                      <div className="form-group">
                        <label>🔢 Raster</label>
                        <select
                          value={editingQuestion.puzzleGridSize || 3}
                          onChange={e => setEditingQuestion({ ...editingQuestion, puzzleGridSize: parseInt(e.target.value) as 3 | 4 | 5 })}
                        >
                          <option value={3}>3×3 (9 stukjes)</option>
                          <option value={4}>4×4 (16 stukjes)</option>
                          <option value={5}>5×5 (25 stukjes)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>⚡ Snelheid</label>
                        <select
                          value={editingQuestion.puzzleSpeed || 'medium'}
                          onChange={e => setEditingQuestion({ ...editingQuestion, puzzleSpeed: e.target.value as 'slow' | 'medium' | 'fast' })}
                        >
                          <option value="slow">🐢 Langzaam (5s)</option>
                          <option value="medium">🚶 Normaal (3s)</option>
                          <option value="fast">🏃 Snel (1.5s)</option>
                        </select>
                      </div>
                    </div>
                    <p className="puzzle-help">💡 De puzzelstukjes verschuiven willekeurig. Kan niet tegelijk met spotlight!</p>
                  </div>
                )}
              </div>
            )}

            {/* Music section */}
            <div className="form-group">
              <label>🎵 Muziek</label>
              <div className="media-section">
                <div className="media-buttons">
                  <button 
                    type="button"
                    className="media-btn spotify-btn"
                    onClick={() => setShowSpotifySearch(true)}
                  >
                    🎧 Zoek op Spotify
                  </button>
                </div>
                
                {/* Spotify search modal */}
                {showSpotifySearch && (
                  <div className="spotify-search-panel">
                    <div className="spotify-search-header">
                      <input
                        type="text"
                        value={spotifyQuery}
                        onChange={e => setSpotifyQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && searchSpotify()}
                        placeholder="Zoek artiest of nummer..."
                        autoFocus
                      />
                      <button onClick={searchSpotify} disabled={spotifyLoading}>
                        {spotifyLoading ? '⏳' : '🔍'}
                      </button>
                      <button onClick={() => { setShowSpotifySearch(false); setSpotifyResults([]); setSpotifyQuery(''); }}>
                        ✕
                      </button>
                    </div>
                    <div className="spotify-results">
                      {spotifyResults.map(track => (
                        <div 
                          key={track.id}
                          className="spotify-result"
                          onClick={() => selectSpotifyTrack(track)}
                        >
                          {track.thumbnail && (
                            <img src={track.thumbnail} alt={track.title} />
                          )}
                          <div className="track-info">
                            <div className="track-title">{track.title}</div>
                            <div className="track-artist">{track.artist}</div>
                          </div>
                          <div className="track-year">{track.year || '?'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Spotify track display */}
                {editingQuestion.spotifyUri && (
                  <div className="selected-media">
                    <span>🎧 {editingQuestion.spotifyUri}</span>
                    <button 
                      type="button"
                      className="clear-btn"
                      onClick={() => setEditingQuestion({ ...editingQuestion, spotifyUri: undefined })}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* YouTube input */}
                <div className="media-url-input">
                  <input
                    type="text"
                    value={editingQuestion.youtubeVideoId || ''}
                    onChange={e => setEditingQuestion({ ...editingQuestion, youtubeVideoId: e.target.value })}
                    placeholder="YouTube Video ID (bijv: dQw4w9WgXcQ)"
                  />
                  {editingQuestion.youtubeVideoId && (
                    <button 
                      type="button"
                      className="clear-btn"
                      onClick={() => setEditingQuestion({ ...editingQuestion, youtubeVideoId: undefined })}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Audio URL input (hidden, auto-filled from Spotify) */}
                {editingQuestion.audioUrl && (
                  <div className="selected-media">
                    <span>🔊 Preview URL beschikbaar</span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Hint (optioneel)</label>
              <input
                type="text"
                value={editingQuestion.hint || ''}
                onChange={e => setEditingQuestion({ ...editingQuestion, hint: e.target.value })}
                placeholder="Optionele hint"
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowQuestionModal(false)}>Annuleren</button>
              <button 
                className="save-btn" 
                onClick={editingQuestion.id ? updateQuestion : addQuestion}
                disabled={!editingQuestion.question || !editingQuestion.correctAnswer}
              >
                {editingQuestion.id ? 'Opslaan' : 'Toevoegen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nieuw Team</h2>
            <div className="form-group">
              <label>Team naam</label>
              <input
                type="text"
                value={editingTeam.name || ''}
                onChange={e => setEditingTeam({ ...editingTeam, name: e.target.value })}
                placeholder="Team naam"
              />
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowTeamModal(false)}>Annuleren</button>
              <button className="save-btn" onClick={addTeam}>Toevoegen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PubQuizPage;
